/**
 * username.ts — Salesforce-style username rules (mirror of the backend's
 * app/features/auth/username.py) plus a debounced availability hook.
 *
 * Model:
 *   - The USERNAME is the login identifier, unique across all of Outrena.
 *   - The EMAIL is a contact address and may be shared by several users.
 *   - Leaving the username blank uses the email as the username.
 *
 * The backend is always the source of truth; these checks only give the
 * admin instant feedback while typing.
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { http } from "@/services/apiClient";

export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 64;
export const EMAIL_USERNAME_MAX_LENGTH = 254;

const USERNAME_PATTERN = /^[a-z0-9](?:[a-z0-9._@+-]*[a-z0-9])?$/;
const EMAIL_SHAPE_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Strip + lowercase; blank becomes "". */
export function normalizeUsername(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

/**
 * Returns an error message, or null when the username is valid.
 * Email-shaped values (incl. the email default) only need to look like an
 * email (≤254 chars); other usernames follow the strict 3–64 char rule.
 */
export function usernameFormatError(value: string): string | null {
  const v = normalizeUsername(value);
  if (!v) return "Username cannot be empty.";
  if (EMAIL_SHAPE_PATTERN.test(v)) {
    return v.length > EMAIL_USERNAME_MAX_LENGTH
      ? `Username must be at most ${EMAIL_USERNAME_MAX_LENGTH} characters.`
      : null;
  }
  if (v.length < USERNAME_MIN_LENGTH) {
    return `Username must be at least ${USERNAME_MIN_LENGTH} characters.`;
  }
  if (v.length > USERNAME_MAX_LENGTH) {
    return `Username must be at most ${USERNAME_MAX_LENGTH} characters.`;
  }
  if (!USERNAME_PATTERN.test(v)) {
    return "Username may contain only letters, digits and . _ - @ + and must start and end with a letter or digit.";
  }
  return null;
}

/** The username that will actually be stored: explicit one, else the email. */
export function effectiveUsername(username: string, email: string): string {
  return normalizeUsername(username) || normalizeUsername(email);
}

/** Pull FastAPI's `detail` out of an axios error for a readable toast. */
export function apiErrorDetail(err: unknown, fallback: string): string {
  const axiosErr = err as AxiosError<{ detail?: unknown }>;
  const detail = axiosErr?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0] as { msg?: unknown };
    if (first && typeof first.msg === "string") {
      return first.msg.replace(/^Value error, /, "");
    }
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export interface UsernameAvailability {
  username: string;
  available: boolean;
  reason: string | null;
}

export type UsernameCheckState =
  | { status: "idle" }
  | { status: "invalid"; message: string }
  | { status: "checking" }
  | { status: "available"; username: string }
  | { status: "taken"; message: string }
  | { status: "error" };

/**
 * Debounced username availability check.
 *
 * @param candidate      the username to check (already resolved — pass the
 *                       email when the username field is blank)
 * @param endpoint       "/api/v1/users/username-available" (tenant admin) or
 *                       "/api/platform/username-available" (super admin)
 * @param excludeUserId  ignore this user's own username (edit dialog)
 * @param enabled        set false to pause checks (e.g. dialog closed)
 */
export function useUsernameAvailability(
  candidate: string,
  endpoint: string,
  excludeUserId?: string,
  enabled = true,
): UsernameCheckState {
  const normalized = normalizeUsername(candidate);
  const [debounced, setDebounced] = useState(normalized);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(normalized), 400);
    return () => window.clearTimeout(handle);
  }, [normalized]);

  const formatError = normalized ? usernameFormatError(normalized) : null;
  const shouldQuery = enabled && !!debounced && !formatError && debounced === normalized;

  const query = useQuery({
    queryKey: ["username-available", endpoint, debounced, excludeUserId ?? ""],
    queryFn: () =>
      http.get<UsernameAvailability>(endpoint, {
        username: debounced,
        ...(excludeUserId ? { excludeUserId } : {}),
      }),
    enabled: shouldQuery,
    staleTime: 10_000,
    retry: false,
  });

  if (!enabled || !normalized) return { status: "idle" };
  if (formatError) return { status: "invalid", message: formatError };
  if (debounced !== normalized || query.isFetching) return { status: "checking" };
  if (query.isError) return { status: "error" };
  if (!query.data) return { status: "checking" };
  return query.data.available
    ? { status: "available", username: query.data.username }
    : { status: "taken", message: query.data.reason ?? "Username is already taken." };
}
